import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Plus, MoreVertical, Pencil, Trash2, Search, Users, Clock, DollarSign, Building2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Labour = () => {
  const [labourItems, setLabourItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLabour, setEditingLabour] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [labourToDelete, setLabourToDelete] = useState(null);
  
  const [form, setForm] = useState({
    name: "",
    description: "",
    hourly_rate: "",
    estimated_hours: "1",
    supplier_id: "",
  });

  const fetchLabourItems = async () => {
    try {
      const response = await axios.get(`${API}/labour`, { withCredentials: true });
      setLabourItems(response.data || []);
    } catch (error) {
      console.error("Error fetching labour items:", error);
      toast.error("Failed to load labour items");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, { withCredentials: true });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  useEffect(() => {
    fetchLabourItems();
    fetchSuppliers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: form.name,
        description: form.description || null,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        estimated_hours: parseFloat(form.estimated_hours) || 1,
        supplier_id: form.supplier_id || null,
      };

      if (editingLabour) {
        await axios.put(`${API}/labour/${editingLabour.labour_id}`, data, { withCredentials: true });
        toast.success("Labour item updated");
      } else {
        await axios.post(`${API}/labour`, data, { withCredentials: true });
        toast.success("Labour item created");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchLabourItems();
    } catch (error) {
      console.error("Error saving labour item:", error);
      toast.error(error.response?.data?.detail || "Failed to save labour item");
    }
  };

  const handleDelete = async () => {
    if (!labourToDelete) return;
    try {
      await axios.delete(`${API}/labour/${labourToDelete.labour_id}`, { withCredentials: true });
      toast.success("Labour item deleted");
      setDeleteDialogOpen(false);
      setLabourToDelete(null);
      fetchLabourItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete labour item");
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      hourly_rate: "",
      estimated_hours: "1",
      supplier_id: "",
    });
    setEditingLabour(null);
  };

  const openEditDialog = (labour) => {
    setEditingLabour(labour);
    setForm({
      name: labour.name,
      description: labour.description || "",
      hourly_rate: labour.hourly_rate?.toString() || "",
      estimated_hours: labour.estimated_hours?.toString() || "1",
      supplier_id: labour.supplier_id || "",
    });
    setDialogOpen(true);
  };

  const filteredLabour = labourItems.filter(item =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.supplier_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="labour-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Labour
          </h1>
          <p className="text-slate-500 mt-1">
            Manage labour types for manufacturing BOMs
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          data-testid="add-labour-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Labour
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-sm">
              <Users className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">{labourItems.length}</p>
              <p className="text-sm text-slate-500">Labour Types</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-sm">
              <DollarSign className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                R{labourItems.reduce((sum, i) => sum + (i.hourly_rate || 0), 0).toFixed(2)}
              </p>
              <p className="text-sm text-slate-500">Total Hourly Value</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-sm">
              <Building2 className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                {labourItems.filter(i => i.supplier_id).length}
              </p>
              <p className="text-sm text-slate-500">With Suppliers</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search labour..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
          />
        </div>
      </div>

      {/* Labour Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {filteredLabour.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Hourly Rate</TableHead>
                  <TableHead className="text-right">Est. Hours</TableHead>
                  <TableHead className="text-right">Est. Cost</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLabour.map((labour) => (
                  <TableRow 
                    key={labour.labour_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openEditDialog(labour)}
                    data-testid={`labour-row-${labour.labour_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-indigo-100 rounded-sm flex items-center justify-center">
                          <Users className="h-4 w-4 text-indigo-600" />
                        </div>
                        <span className="font-medium">{labour.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500 max-w-[200px] truncate">
                      {labour.description || "—"}
                    </TableCell>
                    <TableCell>
                      {labour.supplier_name ? (
                        <Badge variant="outline" className="rounded-sm">
                          {labour.supplier_name}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      R{labour.hourly_rate?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell className="text-right">
                      {labour.estimated_hours || 1} hrs
                    </TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">
                      R{((labour.hourly_rate || 0) * (labour.estimated_hours || 1)).toFixed(2)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(labour)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setLabourToDelete(labour);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-2">No labour items found</p>
              <p className="text-sm text-slate-400">
                Add labour types to use in your manufacturing BOMs
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingLabour ? "Edit Labour" : "Add Labour"}</DialogTitle>
            <DialogDescription>
              {editingLabour ? "Update labour details" : "Create a new labour type for BOMs"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Welding, Assembly, Quality Check"
                required
                className="rounded-sm"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of the labour type"
                rows={2}
                className="rounded-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">Hourly Rate (R)</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.hourly_rate}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                  placeholder="0.00"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated_hours">Est. Hours</Label>
                <Input
                  id="estimated_hours"
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={form.estimated_hours}
                  onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })}
                  placeholder="1"
                  className="rounded-sm"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier (Optional)</Label>
              <Select
                value={form.supplier_id || "none"}
                onValueChange={(value) => setForm({ ...form, supplier_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select supplier (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No supplier</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Assign a supplier for outsourced labour
              </p>
            </div>

            {/* Cost Preview */}
            {form.hourly_rate && form.estimated_hours && (
              <div className="p-3 bg-slate-50 rounded-sm">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Estimated Cost:</span>
                  <span className="font-semibold text-slate-900">
                    R{(parseFloat(form.hourly_rate || 0) * parseFloat(form.estimated_hours || 1)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
            
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-sm">
                {editingLabour ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Labour</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{labourToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Labour;
